import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_laundry-dispatch/artifacts/ce064zn3_Copy%20of%20BusCard%20SB%201.jpg";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, formData);
      // Auth token now lives in httpOnly cookie set by backend.
      localStorage.setItem("admin_logged_in", "true");
      localStorage.setItem("admin_name", response.data.name);
      toast.success(`Welcome back, ${response.data.name}!`);
      navigate("/admin/dashboard");
    } catch (error) {
      logError("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAF9] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Clean Kin" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1A2E12]">Admin Portal</h1>
          <p className="text-[#5C6B57] mt-2">Sign in to manage your business</p>
        </div>

        <Card className="bg-white border-[#E2E8E0] rounded-2xl shadow-natural">
          <CardHeader className="pb-0">
            <CardTitle className="flex items-center gap-2 text-[#1A2E12]">
              <Lock className="w-5 h-5 text-[#7CB342]" strokeWidth={1.5} />
              Secure Login
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-sm font-semibold text-[#2D5016]">Email</Label>
                <Input
                  type="email"
                  placeholder="admin@cleankin.co.uk"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="mt-1 h-12"
                  required
                  data-testid="login-email"
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-[#2D5016]">Password</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="mt-1 h-12"
                  required
                  data-testid="login-password"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full py-6 btn-primary"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-[#E2E8E0]">
              <div className="flex items-center justify-center gap-2 text-sm text-[#5C6B57]">
                <Leaf className="w-4 h-4 text-[#7CB342]" strokeWidth={1.5} />
                <span>Clean Kin Admin System</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-[#5C6B57] mt-6">
          <a href="/" className="text-[#2D5016] hover:underline">← Back to website</a>
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
